import { ApiErrorResponse } from '@gea/digital-ui-lib';

export interface FetchOHistMetadataParams {
  machines: number[];
}

export interface OHistFilesTimeRangeDto {
  fileId?: string | null;
  minimumTS?: string | null;
  maximumTS?: string | null;
}

export interface OHistFilesTimeRange {
  fileId: string;
  minimumTS: Date;
  maximumTS: Date;
}

export interface OHistMetadataDto {
  omniTrackerId?: string | null;
  minimumDateRange?: string | null;
  maximumDateRange?: string | null;
  ohistFilesTimeRanges?: OHistFilesTimeRangeDto[] | null;
}

export interface OHistMetadata {
  omniTrackerId: string;
  availableTimeRange?: string;
  minimumDateRange: Date | null;
  maximumDateRange: Date | null;
  ohistFilesTimeRanges: OHistFilesTimeRange[];
}

export interface OHistReportStateModel {
  metadata: OHistMetadata[];
  isLoading: boolean;
  error: ApiErrorResponse | null;
}

function parseIsoToDateOrNull(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function mapFileRangeDtoToModel(dto: OHistFilesTimeRangeDto): OHistFilesTimeRange {
  const fileId = dto.fileId ?? '';
  const min = parseIsoToDateOrNull(dto.minimumTS);
  const max = parseIsoToDateOrNull(dto.maximumTS);

  if (min === null || max === null) {
    throw new Error('Invalid OHistFilesTimeRangeDto timestamps');
  }

  return {
    fileId,
    minimumTS: min,
    maximumTS: max,
  };
}

export function mapOHistMetadataDtoToModel(dto: OHistMetadataDto): OHistMetadata {
  const rangesDto = dto.ohistFilesTimeRanges ?? [];
  const ranges: OHistFilesTimeRange[] = rangesDto
    .map((r) => {
      try {
        return mapFileRangeDtoToModel(r);
      } catch {
        return null as unknown as OHistFilesTimeRange;
      }
    })
    .filter((r): r is OHistFilesTimeRange => r != null);

  const min = parseIsoToDateOrNull(dto.minimumDateRange ?? null);
  const max = parseIsoToDateOrNull(dto.maximumDateRange ?? null);

  return {
    omniTrackerId: dto.omniTrackerId ?? '',
    minimumDateRange: min,
    maximumDateRange: max,
    availableTimeRange: min && max ? `${formatUtcDDMMYYYY(min)} - ${formatUtcDDMMYYYY(max)}` : 'Not available',
    ohistFilesTimeRanges: ranges,
  };
}

export function formatUtcDDMMYYYY(date: Date): string {
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function mapOHistMetadataModelToDto(model: OHistMetadata): OHistMetadataDto {
  return {
    omniTrackerId: model.omniTrackerId,
    minimumDateRange: model.minimumDateRange ? model.minimumDateRange.toISOString() : null,
    maximumDateRange: model.maximumDateRange ? model.maximumDateRange.toISOString() : null,
    ohistFilesTimeRanges: model.ohistFilesTimeRanges.map((r) => ({
      fileId: r.fileId,
      minimumTS: r.minimumTS.toISOString(),
      maximumTS: r.maximumTS.toISOString(),
    })),
  };
}

export type OhistApiPayload = {
  start_date: string;
  end_date: string;
  omni_tracker_id: string;
  email_id: string;
};
